import { daysOfTheWeekP, generalHelpers, months, typesOfPreaching } from '@monorepo/helpers';
import { FC, useMemo, useState } from 'react';
import { helpers } from 'src/services';
import { HorizontalSelector } from 'src/components/_commons';
import { Modal } from 'react-bootstrap';
import { typePublicMinistryDoc, typePublicMinistryMonthlyService, typePublicMinistryService } from '@monorepo/models';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    publicMinistryDoc: typePublicMinistryDoc;
};

export const PublicMinistryServiceRecordButton: FC<propsType> = ({ publicMinistryDoc }) => {
    const [showModal, setShowModal] = useState(false);
    const isMobile = useGetIsMobile();

    return (
        <div className={'hide-for-navbar'} style={{ position: 'fixed', width: '100%', zIndex: 4 }}>
            {showModal &&
                <ServiceRecord
                    closeModal={() => setShowModal(false)}
                    publicMinistryDoc={publicMinistryDoc}
                />
            }
            <div
                style={{
                    left: '0',
                    marginLeft: '10px',
                    marginTop: '110px',
                    pointerEvents: 'none',
                    position: 'fixed',
                    top: 0,
                    zIndex: 3
                }}
            >
                <div style={{ pointerEvents: 'auto' }}>
                    <button className={`btn btn-general-red d-block mt-2 ${isMobile ? 'btn-sm' : ''}`}
                        onClick={() => setShowModal(true)}
                    >
                        Ver Historial
                    </button>
                </div>
            </div>
        </div>
    )
}

type propsType1 = {
    closeModal: () => void;
    publicMinistryDoc: typePublicMinistryDoc;
};

const ServiceRecord: FC<propsType1> = ({ closeModal, publicMinistryDoc }) => {
    const [month, setMonth] = useState(new Date().getMonth());
    const [year, setYear] = useState(new Date().getFullYear());
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const first = useMemo(() =>
        publicMinistryDoc.monthlyServices.find(ms => month === 1 ?
            ms.month === 11 && ms.year === year - 1
            :
            month === 0 ?
            ms.month === 10 && ms.year === year - 1
            :
            ms.month === month - 2 && ms.year === year
        )
    , [month, publicMinistryDoc, year]);

    const previous = useMemo(() =>
        publicMinistryDoc.monthlyServices.find(ms => month === 0 ?
            ms.month === 11 && ms.year === year - 1
            :
            ms.month === month - 1 && ms.year === year
        ) ?? null
    , [month, publicMinistryDoc, year]);

    const current = useMemo(() =>
        publicMinistryDoc.monthlyServices.find(ms => ms.month === month && ms.year === year) ?? null
    , [month, publicMinistryDoc, year]);

    const next = useMemo(() =>
        publicMinistryDoc.monthlyServices.find(ms => month === 11 ?
            ms.month === 0 && ms.year === year + 1
            :
            ms.month === month + 1 && ms.year === year
        )
    , [month, publicMinistryDoc, year]);

    const getCellData = (monthlyService: typePublicMinistryMonthlyService, s: typePublicMinistryService): string => {
        return (`${s.typeOfRegularity < 3 ?
                `Los ${daysOfTheWeekP[s.weekday!]} a las ${String(s.hour).padStart(2, '0')}:${String(s.minutes).padStart(2, '0')} hs.`
                :
                `El día ${helpers.GetDayOfWeekFromDateString(generalHelpers.FormatDate(new Date(monthlyService.year, monthlyService.month, s.day!)))} ${s.day}`
            }
            ${!!s.isCircuitOverseer ? 'durante la visita' : ''}
            ${!!s.groups.length ?
                (s.groups.length === 1 ?
                    ` por el grupo ${s.groups[0]}`
                    :
                    ` por los grupos ${s.groups!.join(', ')}`
                )
                :
                ''
            }
            ${s.typeOfPreaching === 'house-to-house' ? '' : ` (${typesOfPreaching.find(tp => tp.type === s.typeOfPreaching)?.label})`}
        `);
    }

    return (
        <Modal show fullscreen onHide={closeModal}>
            <Modal.Header className={isDarkMode ? 'bg-dark text-white border-white' : ''} closeButton>
                <h3 className={'mb-0'}>
                    REGISTROS DE PREDICACIÓN
                </h3>
            </Modal.Header>
            <Modal.Body className={isDarkMode ? 'bg-dark border-white' : ''}>
                <HorizontalSelector
                    label={`${months[month]} ${year}`}
                    setNext={() => {
                        if (month === 11) {
                            setMonth(0);
                            setYear(x => x + 1);
                        } else {
                            setMonth(x => x + 1);
                        }
                    }}
                    setPrevious={() => {
                        if (month === 0) {
                            setMonth(11);
                            setYear(x => x - 1);
                        } else {
                            setMonth(x => x - 1);
                        }
                    }}
                />
                <div className={'table-responsive mt-3'}>
                    <table className={`table table-striped table-bordered ${isDarkMode ? 'table-dark border' : ''} ${isMobile ? 'small' : ''}`}
                        style={{ tableLayout: 'fixed' }}
                    >
                        <colgroup>
                            <col style={{ width: isMobile ? '60px' : '120px' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>{isMobile ? 'TERR.' : 'TERRITORIO'}</th>
                                <th>{months[month === 1 ? 11 : month === 0 ? 10 : month - 2].toUpperCase()} {month < 2 ? year - 1 : year}</th>
                                <th>{months[month === 0 ? 11 : month - 1].toUpperCase()} {month === 0 ? year - 1 : year}</th>
                                <th>{months[month].toUpperCase()} {year}</th>
                                {!isMobile &&
                                    <th>{months[month === 11 ? 0 : month + 1].toUpperCase()} {month === 11 ? year + 1 : year}</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: config.numberOfTerritories }, (_, i) => i + 1).map(t =>
                                <tr key={t}>
                                    <td>
                                        {t}
                                    </td>
                                    <td>
                                        {first?.services.filter(s => s.territories.includes(t)).map(s =>
                                            <div key={s.id}>
                                                {getCellData(first, s)}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {previous?.services.filter(s => s.territories.includes(t)).map(s =>
                                            <div key={s.id}>
                                                {getCellData(previous, s)}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {current?.services.filter(s => s.territories.includes(t)).map(s =>
                                            <div key={s.id}>
                                                {getCellData(current, s)}
                                            </div>
                                        )}
                                    </td>
                                    {!isMobile &&
                                        <td>
                                            {next?.services.filter(s => s.territories.includes(t)).map(s =>
                                                <div key={s.id}>
                                                    {getCellData(next, s)}
                                                </div>
                                            )}
                                        </td>
                                    }
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>
        </Modal>
    )
}
